import React, { useState } from 'react';
import { Education as EDUCATION } from '@axiom/const';
import {
  Badge,
  Button,
  CondensedLabel,
  CondensedLarge,
  CondensedMedium,
  ContextMenu,
  ContextMenuItem,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  ToastUtil,
  Well,
} from '@axiom/ui';
import { isEnvoyAdmin } from '@axiom/utils';
import type {
  Candidate,
  CandidateRedux,
  Education,
  EducationEdit,
  User,
} from '@axiom/validation';

import { EducationApi } from '../../api/education-api';
import { CandidateApi } from '../../api/candidate-api';

export type EducationItemProps = {
  education: Education;
  onEdit: () => void;
  user: User;
  candidate: CandidateRedux | Candidate;
  name: string;
};

export const EducationItem = ({
  education,
  onEdit,
  user,
  candidate,
  name,
}: EducationItemProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [
    isConfirmingGraduationYearRemove,
    setIsConfirmingGraduationYearRemove,
  ] = useState(false);

  const handleSetPrimary = async () => {
    if (education.isPrimary) {
      return;
    }
    const updateData: Partial<EducationEdit> = {
      areaOfStudyId: education.areaOfStudyId,
      candidateId: education.candidateId,
      lawSchoolId: education.lawSchoolId,
      isPrimary: true,
    };
    await EducationApi.updateEducation(education.id, updateData);
    await CandidateApi.refreshCandidate({ candidateId: candidate.id });
  };

  const handleDelete = async () => {
    await EducationApi.deleteEducation(education.id);
    await CandidateApi.refreshCandidate({ candidateId: candidate.id });
    setIsConfirmingDelete(false);
  };

  const handleRemoveGraduationYear = async () => {
    const updateData: Partial<EducationEdit> = {
      areaOfStudyId: education.areaOfStudyId,
      candidateId: education.candidateId,
      lawSchoolId: education.lawSchoolId,
      degree: education.degree ?? undefined,
      isPrimary: education.isPrimary,
      yearAwarded: null,
    };
    await EducationApi.updateEducation(education.id, updateData);
    await CandidateApi.refreshCandidate({ candidateId: candidate.id });
    setIsConfirmingGraduationYearRemove(false);
  };

  const handleRemoveGraduationYearClick = () => {
    if (isEnvoyAdmin(user)) {
      setIsConfirmingGraduationYearRemove(true);
    } else {
      ToastUtil.add({
        name: 'EDUCATION_REMOVE_GRADUATION_YEAR_ERROR',
        type: 'error',
        dismissible: true,
        children: (
          <CondensedLarge>
            Only admins can remove graduation year.
          </CondensedLarge>
        ),
      });
    }
  };

  return (
    <Gutter vertical="8px">
      <Well name={name}>
        <Grid>
          <GridRow verticalAlign="top">
            <GridColumn width={6}>
              <CondensedLabel name="INSTITUTION_LABEL">
                Institution
              </CondensedLabel>
              <CondensedMedium name="INSTITUTION_VALUE" maxLines={1}>
                {education.institution}
              </CondensedMedium>
            </GridColumn>
            <GridColumn width={6}>
              <Layout direction="horizontal" position="top right">
                <LayoutItem name="DATE">
                  <CondensedLabel name="DATE_LABEL">Date</CondensedLabel>
                  <CondensedMedium name="DATE_VALUE">
                    {education.yearAwarded ?? '--'}
                  </CondensedMedium>
                </LayoutItem>
                <LayoutItem>
                  <Gutter left="8px">
                    <ContextMenu name="EDUCATION_ITEM_MENU" direction="left">
                      {!education.isPrimary && (
                        <ContextMenuItem
                          name="SET_PRIMARY"
                          onClick={handleSetPrimary}
                        >
                          Set Primary
                        </ContextMenuItem>
                      )}
                      <ContextMenuItem name="EDIT" onClick={onEdit}>
                        Edit
                      </ContextMenuItem>
                      {!education.isPrimary && (
                        <ContextMenuItem
                          name="DELETE"
                          onClick={() => {
                            setIsConfirmingDelete(true);
                          }}
                        >
                          Delete
                        </ContextMenuItem>
                      )}
                      {education.yearAwarded ? (
                        <ContextMenuItem
                          name="REMOVE_GRADUATION_YEAR"
                          onClick={handleRemoveGraduationYearClick}
                        >
                          Remove graduation year
                        </ContextMenuItem>
                      ) : null}
                    </ContextMenu>
                  </Gutter>
                </LayoutItem>
              </Layout>
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn width={8}>
              <Gutter top="8px">
                <CondensedLabel>Degree</CondensedLabel>
                <CondensedMedium name="DEGREE_VALUE">
                  {education.degree
                    ? EDUCATION.DEGREES[education.degree]
                    : '--'}
                </CondensedMedium>
              </Gutter>
              {education.areaOfStudy ? (
                <Gutter top="8px">
                  <CondensedLabel>Area Of Study</CondensedLabel>
                  <CondensedMedium name="AREA_OF_STUDY_VALUE">
                    {education.areaOfStudy.name}
                  </CondensedMedium>
                </Gutter>
              ) : null}
            </GridColumn>
            <GridColumn width={4} verticalAlign="middle">
              {education.isPrimary ? (
                <Gutter top="8px">
                  <Layout position="right middle">
                    <Badge name="PRIMARY_BADGE" background="yellow">
                      Primary
                    </Badge>
                  </Layout>
                </Gutter>
              ) : null}
            </GridColumn>
          </GridRow>
        </Grid>
      </Well>

      {isConfirmingDelete ? (
        <Modal name="DELETE_CONFIRMATION_MODAL" size="confirm">
          <ModalHeader
            name="DELETE_CONFIRMATION_MODAL_HEADER"
            onClose={() => {
              setIsConfirmingDelete(false);
            }}
          >
            Delete Education
          </ModalHeader>
          <ModalSection>
            <Paragraph name="DELETE_CONFIRMATION_MESSAGE">
              Are you sure you want to delete "{education.degree}" from "
              {education.institution}"?
            </Paragraph>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_DELETE"
              variation="outline"
              onClick={() => {
                setIsConfirmingDelete(false);
              }}
            >
              Cancel
            </Button>
            <Button name="CONFIRM_DELETE" onClick={handleDelete}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}

      {isConfirmingGraduationYearRemove ? (
        <Modal name="REMOVE_GRADUATION_YEAR_MODAL" size="confirm">
          <ModalHeader
            name="REMOVE_GRADUATION_YEAR_MODAL_HEADER"
            onClose={() => {
              setIsConfirmingGraduationYearRemove(false);
            }}
          >
            Remove graduation year
          </ModalHeader>
          <ModalSection>
            <Paragraph name="REMOVE_GRADUATION_YEAR_MESSAGE">
              This will permanently remove the graduation year from this
              education record.
            </Paragraph>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_REMOVE_GRADUATION_YEAR"
              variation="outline"
              onClick={() => {
                setIsConfirmingGraduationYearRemove(false);
              }}
            >
              Cancel
            </Button>
            <Button
              name="CONFIRM_REMOVE_GRADUATION_YEAR"
              onClick={handleRemoveGraduationYear}
            >
              Remove
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </Gutter>
  );
};
